import type { Question } from './topics';
import { options, pick } from './util';

// ─────────────────────────────────────────────────────────────────────────────
// English, for students learning it. Third-person -s by rule; past tenses
// from checked lists (doubling rules have too many exceptions to generate).
// ─────────────────────────────────────────────────────────────────────────────

type Subj = { w: string; third: boolean; be: 'am' | 'is' | 'are' };
const SUBJECTS: Subj[] = [
  { w: 'I', third: false, be: 'am' }, { w: 'You', third: false, be: 'are' },
  { w: 'He', third: true, be: 'is' }, { w: 'She', third: true, be: 'is' }, { w: 'It', third: true, be: 'is' },
  { w: 'We', third: false, be: 'are' }, { w: 'They', third: false, be: 'are' },
  { w: 'My brother', third: true, be: 'is' }, { w: 'The students', third: false, be: 'are' },
  { w: 'My parents', third: false, be: 'are' }, { w: 'Maria', third: true, be: 'is' },
];

const ADJECTIVES = ['happy', 'tired', 'hungry', 'at school', 'from Mexico', 'very tall', 'ready', 'late again', 'in the kitchen', 'excited'];

export function enBe(n: number): Question[] {
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const s = pick(SUBJECTS.filter(x => x.w !== 'It'));
    const rest = pick(ADJECTIVES);
    qs.push({
      key: `en_be:${s.w}:${rest}`, topic: 'en_be', kind: 'choice', instruction: 'Am, is or are?',
      prompt: `${s.w} ___ ${rest}.`, options: ['am', 'is', 'are'], answers: [s.be],
      why: s.be === 'am' ? 'I → am' : s.be === 'is' ? 'One person or thing → is' : 'You, we, they, or more than one → are',
    });
  }
  return qs;
}

const VERBS: { v: string; obj: string }[] = [
  { v: 'play', obj: 'soccer after school' }, { v: 'watch', obj: 'TV at night' }, { v: 'go', obj: 'to school by bus' },
  { v: 'do', obj: 'homework every day' }, { v: 'study', obj: 'in the library' }, { v: 'have', obj: 'a big dog' },
  { v: 'live', obj: 'near the park' }, { v: 'like', obj: 'pizza' }, { v: 'eat', obj: 'breakfast at seven' },
  { v: 'drink', obj: 'coffee in the morning' }, { v: 'read', obj: 'a lot of books' }, { v: 'work', obj: 'at a hospital' },
  { v: 'fix', obj: 'cars' }, { v: 'wash', obj: 'the dishes' }, { v: 'try', obj: 'new food' },
  { v: 'teach', obj: 'math' }, { v: 'carry', obj: 'a heavy backpack' }, { v: 'miss', obj: 'the bus sometimes' },
  { v: 'buy', obj: 'bread every morning' }, { v: 'say', obj: 'hello to everyone' },
];

/** He / she / it: plays, watches, goes, studies, has. */
export function thirdPerson(v: string): string {
  if (v === 'have') return 'has';
  if (/(s|sh|ch|x|z|o)$/.test(v)) return v + 'es';
  if (/[^aeiou]y$/.test(v)) return v.slice(0, -1) + 'ies';
  return v + 's';
}

export function enPresentS(n: number): Question[] {
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const s = pick(SUBJECTS.filter(x => x.w !== 'It'));
    const x = pick(VERBS);
    const answer = s.third ? thirdPerson(x.v) : x.v;
    const typed = Math.random() < 0.5;
    qs.push({
      key: `en_present_s:${s.w}:${x.v}`, topic: 'en_present_s', kind: typed ? 'type' : 'choice',
      instruction: typed ? 'Present simple · write the verb' : 'Present simple · choose the verb',
      prompt: `${s.w} ___ (${x.v}) ${x.obj}.`, answers: [answer],
      why: s.third ? `He / she / it → ${thirdPerson(x.v)}` : `${s.w} → ${x.v} (no -s)`,
      options: options(answer, [x.v, thirdPerson(x.v), `${x.v}ing`]),
    });
  }
  return qs;
}

const REGULAR_PAST: [string, string, string][] = [
  ['play', 'played', 'soccer'], ['watch', 'watched', 'a movie'], ['study', 'studied', 'for the test'],
  ['stop', 'stopped', 'at the store'], ['like', 'liked', 'the concert'], ['walk', 'walked', 'to school'],
  ['cook', 'cooked', 'dinner'], ['clean', 'cleaned', 'my room'], ['visit', 'visited', 'my grandma'],
  ['plan', 'planned', 'a party'], ['try', 'tried', 'sushi'], ['dance', 'danced', 'all night'],
  ['carry', 'carried', 'the boxes'], ['start', 'started', 'a new book'], ['finish', 'finished', 'my homework'],
  ['want', 'wanted', 'a new phone'], ['open', 'opened', 'the window'], ['shop', 'shopped', 'at the mall'],
  ['cry', 'cried', 'at the end of the movie'], ['travel', 'traveled', 'to Canada'],
];

const IRREGULAR_PAST: [string, string, string][] = [
  ['go', 'went', 'to the beach'], ['have', 'had', 'a great time'], ['eat', 'ate', 'tacos'], ['see', 'saw', 'a whale'],
  ['make', 'made', 'a cake'], ['take', 'took', 'the bus'], ['come', 'came', 'home late'], ['get', 'got', 'a new job'],
  ['give', 'gave', 'her a gift'], ['write', 'wrote', 'a letter'], ['buy', 'bought', 'new shoes'],
  ['think', 'thought', 'about it'], ['drink', 'drank', 'some water'], ['sleep', 'slept', 'for ten hours'],
  ['swim', 'swam', 'in the lake'], ['run', 'ran', 'five kilometers'], ['speak', 'spoke', 'to the teacher'],
  ['find', 'found', 'my keys'], ['do', 'did', 'the dishes'], ['say', 'said', 'goodbye'], ['leave', 'left', 'early'],
  ['begin', 'began', 'a new class'], ['bring', 'brought', 'snacks'], ['teach', 'taught', 'English'],
  ['catch', 'caught', 'a fish'], ['fly', 'flew', 'to Miami'], ['drive', 'drove', 'to work'], ['sing', 'sang', 'a song'],
];

function past(topic: 'en_past_regular' | 'en_past_irregular', list: [string, string, string][], n: number): Question[] {
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const [v, p, obj] = pick(list);
    const s = pick(SUBJECTS.filter(x => x.w !== 'It'));
    const typed = Math.random() < 0.6;
    // Real mistakes only: stoped, studyed, runned, buyed — never nonsense like playeded.
    const wrong = topic === 'en_past_irregular' ? [`${v}ed`, v, `${v}s`] : [`${v}ed`, `${v}d`, v, `${v}s`];
    qs.push({
      key: `${topic}:${v}:${s.w}`, topic, kind: typed ? 'type' : 'choice',
      instruction: typed ? 'Past simple · write the verb' : 'Past simple · choose the verb',
      prompt: `Yesterday, ${s.w === 'I' ? 'I' : s.w.toLowerCase().replace(/^maria$/, 'Maria')} ___ (${v}) ${obj}.`, answers: [p],
      options: options(p, wrong.filter(w => w !== p)),
    });
  }
  return qs;
}

export const enPastRegular = (n: number) => past('en_past_regular', REGULAR_PAST, n);
export const enPastIrregular = (n: number) => past('en_past_irregular', IRREGULAR_PAST, n);

/** A / an goes by SOUND, not spelling — the traps are the point. */
const A_AN: { w: string; a: 'a' | 'an'; trap?: string }[] = [
  { w: 'apple', a: 'an' }, { w: 'banana', a: 'a' }, { w: 'egg', a: 'an' }, { w: 'orange', a: 'an' },
  { w: 'house', a: 'a' }, { w: 'elephant', a: 'an' }, { w: 'idea', a: 'an' }, { w: 'dog', a: 'a' },
  { w: 'island', a: 'an' }, { w: 'hotel', a: 'a' }, { w: 'year', a: 'a' }, { w: 'umbrella', a: 'an' },
  { w: 'interesting book', a: 'an' }, { w: 'big apple', a: 'a' }, { w: 'old car', a: 'an' },
  { w: 'hour', a: 'an', trap: 'The h is silent: "an our"' },
  { w: 'honest answer', a: 'an', trap: 'The h is silent' },
  { w: 'university', a: 'a', trap: 'It starts with a "you" sound' },
  { w: 'uniform', a: 'a', trap: 'It starts with a "you" sound' },
  { w: 'European city', a: 'a', trap: 'It starts with a "you" sound' },
  { w: 'one-way ticket', a: 'a', trap: 'It starts with a "w" sound' },
  { w: 'useful tool', a: 'a', trap: 'It starts with a "you" sound' },
  { w: 'unusual day', a: 'an', trap: 'It starts with an "uh" sound' },
];

export function enArticles(n: number): Question[] {
  const traps = A_AN.filter(x => x.trap);
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const x = Math.random() < 0.45 ? pick(traps) : pick(A_AN);
    qs.push({
      key: `en_articles:${x.w}`, topic: 'en_articles', kind: 'choice', instruction: 'A or an?',
      prompt: `It's ___ ${x.w}.`, options: ['a', 'an'], answers: [x.a],
      why: x.trap ?? (x.a === 'an' ? 'Starts with a vowel sound → an' : 'Starts with a consonant sound → a'),
    });
  }
  return qs;
}
